'use client';

// Perspective marquee (21st.dev / Remocn) adaptado pra web: o motor de animacao
// (remotion useCurrentFrame) foi trocado por requestAnimationFrame baseado em tempo,
// mantendo o visual identico (perspectiva 3D + blur/opacidade nas pontas + fades).
// Renderiza logos (SVG) em vez de texto.

import { useEffect, useRef, useState } from 'react';

export interface MarqueeItem {
  src: string;
  alt: string;
}

export interface PerspectiveMarqueeProps {
  items: MarqueeItem[];
  iconHeight?: number;
  slotWidth?: number;
  pixelsPerFrame?: number;
  rotateY?: number;
  rotateX?: number;
  perspective?: number;
  fadeColor?: string;
  background?: string;
  speed?: number;
  className?: string;
}

export function PerspectiveMarquee({
  items,
  iconHeight = 46,
  slotWidth = 180,
  pixelsPerFrame = 2,
  rotateY = -28,
  rotateX = 8,
  perspective = 1200,
  fadeColor = '#08060c',
  background = 'transparent',
  speed = 1,
  className,
}: PerspectiveMarqueeProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [frame, setFrame] = useState(0);
  const [width, setWidth] = useState(1280);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth || 1280));
    ro.observe(el);
    setWidth(el.clientWidth || 1280);

    let raf = 0;
    let start: number | null = null;
    let inView = true;
    const io = new IntersectionObserver(
      (e) => {
        inView = e[0]?.isIntersecting ?? true;
      },
      { threshold: 0 },
    );
    io.observe(el);

    const loop = (t: number): void => {
      raf = requestAnimationFrame(loop);
      if (!inView || document.hidden) {
        start = t;
        return;
      }
      if (start === null) start = t;
      setFrame(((t - start) / (1000 / 60)) * speed);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
    };
  }, [speed]);

  const center = width / 2;
  const totalWidth = slotWidth * items.length;
  const offset = -((frame * pixelsPerFrame) % totalWidth);
  const rendered = [...items, ...items, ...items];

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        perspective: `${perspective}px`,
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
          transformStyle: 'preserve-3d',
        }}
      >
        <div
          style={{
            display: 'flex',
            whiteSpace: 'nowrap',
            transform: `translateX(${offset}px)`,
          }}
        >
          {rendered.map((item, i) => {
            const itemCenter = i * slotWidth + slotWidth / 2 + offset;
            const norm = center === 0 ? 0 : (itemCenter - center) / center;
            const distance = Math.min(1, Math.abs(norm));
            const blurPx = distance * 6;
            const opacity = 1 - distance * 0.4;

            return (
              <span
                key={i}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: slotWidth,
                  filter: `blur(${blurPx}px)`,
                  opacity,
                }}
              >
                <img
                  src={item.src}
                  alt={item.alt}
                  height={iconHeight}
                  style={{ height: iconHeight, width: 'auto', objectFit: 'contain' }}
                  draggable={false}
                />
              </span>
            );
          })}
        </div>
      </div>

      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `linear-gradient(90deg, ${fadeColor} 0%, transparent 18%, transparent 82%, ${fadeColor} 100%)`,
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `linear-gradient(180deg, ${fadeColor} 0%, transparent 25%, transparent 75%, ${fadeColor} 100%)`,
        }}
      />
    </div>
  );
}

export default PerspectiveMarquee;
